import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { PlusCircle, MinusCircle, Edit, Trash2, ShoppingCart, X, Check, Package, LogOut, TrendingUp, Search, ChevronDown, ChevronRight, ChevronsDown, ChevronsUp, Building2, Paperclip, Download, Upload, MoreVertical } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './components/ui/card';
import { getProducts, getProductById, getCategories, addProduct, updateProduct, deleteProduct, updateStock, addCategory, getAllUsers, getSuppliers, getProductsMultiStructures, searchProductsInOtherStructures, uploadProductFile, deleteProductFile } from './api';
import { useStructure } from './contexts/StructureContext';
import { usePersonContext } from './contexts/PersonContext';
import PriceHistory from './components/PriceHistory';
import DailyMovements from './components/DailyMovements';
import ConfirmDialog from './components/ConfirmDialog';
import LoadingSpinner from './components/LoadingSpinner';
import { useToast } from './contexts/NotificationContext';

// Données de secours au cas où l'API échoue
const fallbackProducts = [];
/*const fallbackProducts = [
  { id: 1, name: "Gel hydroalcoolique", category: "Produits d'entretien", currentStock: 8, idealStock: 10, history: [] },
  { id: 2, name: "Essuie-tout", category: "Produits d'entretien", currentStock: 4, idealStock: 12, history: [] },
  { id: 3, name: "Couches taille 3", category: "Couches", currentStock: 2, idealStock: 20, history: [] },
  { id: 4, name: "Stylos bleus", category: "Papeterie", currentStock: 15, idealStock: 10, history: [] },
  { id: 5, name: "Papier A4", category: "Papeterie", currentStock: 0, idealStock: 5, history: [] },
];*/
const fallbackCategories = [];
//const fallbackCategories = ["Produits d'entretien", "Papeterie", "Couches", "Alimentaire"];

const StockManagementApp = ({ user, onLogout, showAllStructures, setShowAllStructures, navPayload, onNavPayloadConsumed }) => {
  const { structures, currentStructure, loading: structureLoading, error: structureError } = useStructure();
  const { currentPerson } = usePersonContext();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showUpdateStock, setShowUpdateStock] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [allStructuresProducts, setAllStructuresProducts] = useState([]);
  const [loadingAllStructures, setLoadingAllStructures] = useState(false);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [fileModalProduct, setFileModalProduct] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [actionMenu, setActionMenu] = useState(null); // { productId, product, x, y }
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showShoppingList, setShowShoppingList] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: "",
    category: "",
    idealStock: 2,
    currentStock: 0,
    supplier: "",
    currentPrice: ""
  });
  const [currentUser, setCurrentUser] = useState(user?.name || "Admin");
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // États pour les quantités personnalisées
  const [customAmount, setCustomAmount] = useState("");
  const [customRemoveAmount, setCustomRemoveAmount] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [priceHistoryProduct, setPriceHistoryProduct] = useState(null);
  // Changelog and Settings are now controlled by AppLayout via props
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger'
  });
  const [actionLoading, setActionLoading] = useState(false);

  // Hook pour les notifications (contexte global)
  const { success, error: notifyError, warning, info } = useToast();

  // Fermer le menu contextuel quand on clique ailleurs ou scroll
  const closeActionMenu = useCallback(() => setActionMenu(null), []);
  useEffect(() => {
    if (!actionMenu) return;
    const handleClick = (e) => {
      // Ne pas fermer si on clique dans le menu lui-même
      const menuEl = document.getElementById('action-menu-portal');
      if (menuEl && menuEl.contains(e.target)) return;
      setActionMenu(null);
    };
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', closeActionMenu, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', closeActionMenu, true);
    };
  }, [actionMenu, closeActionMenu]);

  const openActionMenu = useCallback((e, product) => {
    e.stopPropagation();
    e.preventDefault();
    if (actionMenu?.productId === product.id) {
      setActionMenu(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setActionMenu({
      productId: product.id,
      product,
      x: rect.right,
      y: rect.bottom + 4,
    });
  }, [actionMenu]);

  // Charger les données depuis l'API au démarrage
  useEffect(() => {
	  if (!currentStructure) {
		return;
	  }

    const fetchData = async () => {
      try {
        setLoading(true);
        
		// Récupérer les fournisseurs
		const suppliersResponse = await getSuppliers(currentStructure.id);
		setSuppliers(suppliersResponse.data.filter(s => s.active));

        // Récupérer les catégories d'abord
        const categoriesResponse = await getCategories(currentStructure.id);
        const fetchedCategories = categoriesResponse.data;
        setCategories(fetchedCategories);
        
        // Récupérer les produits ensuite
        const productsResponse = await getProducts(currentStructure.id);
        const fetchedProducts = productsResponse.data;
        setProducts(fetchedProducts);
		

        // Récupérer les utilisateurs
        // TODO: Fix getAllUsers route in backend - currently returns 404
        // const usersResponse = await getAllUsers(currentStructure.id);
        // setUsers(usersResponse.data);

        // Sélectionner l'utilisateur courant par défaut
        // const currentUserInList = usersResponse.data.find(u => u.email === user.email);
        // setSelectedUser(currentUserInList || (usersResponse.data.length > 0 ? usersResponse.data[0] : null));

        // Initialiser le formulaire de nouveau produit avec la première catégorie
        if (fetchedCategories.length > 0) {
          setNewProduct(prev => ({
            ...prev,
            category: fetchedCategories[0].id
          }));
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        setError("Erreur de connexion au serveur. Utilisation des données locales.");
        
        // Utiliser les données de secours en cas d'erreur
        setProducts(fallbackProducts);
        setCategories(fallbackCategories);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentStructure, user]);

  // Auto-ouverture fiche produit/fournisseur depuis recherche globale
  useEffect(() => {
    if (!navPayload) return;
    if (navPayload.productId && products.length > 0) {
      const p = products.find(pr => pr.id === navPayload.productId);
      if (p) {
        handleEditProduct(p);
        onNavPayloadConsumed?.();
      }
    } else if (navPayload.supplierId) {
      // Pas de modal fiche fournisseur dédiée — on bascule juste sur la vue produit
      // qui montre les fournisseurs liés. Idéal : ouvrir un modal admin > fournisseurs.
      // Pour l'instant on consomme silencieusement le payload.
      onNavPayloadConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navPayload, products]);

  // Rechercher des suggestions cross-structure lors de la saisie du nom produit
  useEffect(() => {
    if (!showAddForm || newProduct.id || structures.length < 2 || !currentStructure) {
      setProductSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (newProduct.name.length < 3) {
      setProductSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const response = await searchProductsInOtherStructures(newProduct.name, currentStructure.id);
        setProductSuggestions(response.data);
        setShowSuggestions(response.data.length > 0);
      } catch (err) {
        console.error("Erreur recherche suggestions:", err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [newProduct.name, showAddForm, newProduct.id, structures, currentStructure]);

  // Charger les produits multi-structures quand le toggle est activé
  useEffect(() => {
    if (!showAllStructures || structures.length < 2) return;

    const fetchAllStructuresProducts = async () => {
      try {
        setLoadingAllStructures(true);
        const structureIds = structures.map(s => s.id);
        const response = await getProductsMultiStructures(structureIds);
        setAllStructuresProducts(response.data);
      } catch (err) {
        console.error("Erreur chargement multi-structures:", err);
        notifyError("Erreur lors du chargement des produits multi-structures");
      } finally {
        setLoadingAllStructures(false);
      }
    };

    fetchAllStructuresProducts();
  }, [showAllStructures, structures]);

  // Produits actifs selon le mode (mono ou multi-structure)
  const activeProducts = showAllStructures ? allStructuresProducts : products;

  // Liste des catégories disponibles (pour le filtre dropdown)
  const availableCategoryNames = [...new Set(activeProducts.map(p => {
    return typeof p.category === 'object'
      ? p.category?.name || 'Sans catégorie'
      : getCategoryName(p.category) || 'Sans catégorie';
  }))].sort((a, b) => {
    if (a === 'Sans catégorie') return 1;
    if (b === 'Sans catégorie') return -1;
    return a.localeCompare(b);
  });

  // Filtrer par recherche texte, catégorie, et trier les produits
  const filteredProducts = activeProducts
    .filter(product => {
      if (searchText && !product.name.toLowerCase().includes(searchText.toLowerCase())) return false;
      if (filterCategory !== "all") {
        const catName = typeof product.category === 'object'
          ? product.category?.name || 'Sans catégorie'
          : getCategoryName(product.category) || 'Sans catégorie';
        if (catName !== filterCategory) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const factor = sortOrder === "asc" ? 1 : -1;
      if (sortBy === "name") {
        return factor * a.name.localeCompare(b.name);
      }
      return factor * (a[sortBy] - b[sortBy]);
    });

  // En mode multi-structures : grouper par structure puis par catégorie
  const productsByStructure = showAllStructures
    ? filteredProducts.reduce((acc, product) => {
        const structName = product.structureName || 'Structure inconnue';
        if (!acc[structName]) acc[structName] = [];
        acc[structName].push(product);
        return acc;
      }, {})
    : null;

  const sortedStructureNames = productsByStructure
    ? Object.keys(productsByStructure).sort((a, b) => a.localeCompare(b))
    : [];

  // Obtenir le nom de la catégorie à partir de l'ID ou de l'objet
  const getCategoryName = (category) => {
    if (!category) return "";
    if (typeof category === 'string') {
      const found = categories.find(c => c.id === category);
      return found ? found.name : category;
    }
    return category.name || "";
  };

  // Grouper les produits filtrés par catégorie (pour une structure ou toutes)
  const groupByCategory = (prods) => {
    const grouped = prods.reduce((acc, product) => {
      const catName = typeof product.category === 'object'
        ? product.category?.name || 'Sans catégorie'
        : getCategoryName(product.category) || 'Sans catégorie';
      if (!acc[catName]) acc[catName] = [];
      acc[catName].push(product);
      return acc;
    }, {});
    return grouped;
  };

  const productsByCategory = showAllStructures ? null : groupByCategory(filteredProducts);

  // Trier les catégories (alphabétique, "Sans catégorie" en dernier)
  const sortCategoryNames = (grouped) => Object.keys(grouped).sort((a, b) => {
    if (a === 'Sans catégorie') return 1;
    if (b === 'Sans catégorie') return -1;
    return a.localeCompare(b);
  });

  const sortedCategoryNames = productsByCategory ? sortCategoryNames(productsByCategory) : [];

  // Toggle une section (catégorie ou structure)
  const toggleCategory = (catName) => {
    setCollapsedCategories(prev => ({ ...prev, [catName]: !prev[catName] }));
  };

  const collapseAll = () => {
    const all = {};
    if (showAllStructures) {
      sortedStructureNames.forEach(sName => {
        all[`struct_${sName}`] = true;
        const catGrouped = groupByCategory(productsByStructure[sName]);
        Object.keys(catGrouped).forEach(cName => { all[`${sName}_${cName}`] = true; });
      });
    } else {
      sortedCategoryNames.forEach(name => { all[name] = true; });
    }
    setCollapsedCategories(all);
  };

  const expandAll = () => {
    setCollapsedCategories({});
  };

  // Reset collapsed categories when multi-structures toggle changes
  useEffect(() => {
    setCollapsedCategories({});
  }, [showAllStructures]);

  // Générer la liste de courses
  const shoppingList = products.filter(p => p.currentStock < p.idealStock)
    .map(p => ({
      ...p,
      toOrder: p.idealStock - p.currentStock
    }));

  // Ajouter ou mettre à jour un produit
  const handleAddProduct = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      if (newProduct.id) {
        // Mise à jour d'un produit existant
        const response = await updateProduct(newProduct.id, {
		  ...newProduct,
		  structureId: currentStructure.id
		});
        setProducts(products.map(p => p.id === newProduct.id ? response.data.product : p));
      } else {
        // Ajout d'un nouveau produit
        const response = await addProduct({
		  ...newProduct,
		  structureId: currentStructure.id
		});
        setProducts([...products, response.data.product]);
		await refreshProducts();
      }

      setNewProduct({
        name: "",
        category: categories.length > 0 ? categories[0].id : "",
        idealStock: 10,
        currentStock: 0
      });
      setShowAddForm(false);
      success("Produit enregistré avec succès");
    } catch (error) {
      console.error("Erreur lors de l'ajout/modification du produit:", error);
      notifyError("Erreur lors de l'ajout/modification du produit");
    } finally {
      setActionLoading(false);
    }
  };

  // Supprimer un produit
  const handleDeleteProduct = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Supprimer le produit",
      message: "Êtes-vous sûr de vouloir supprimer ce produit ? Cette action est irréversible.",
      variant: "danger",
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        setActionLoading(true);
        try {
          await deleteProduct(id, currentStructure.id);
          await refreshProducts();
          setProducts(products.filter(p => p.id !== id));
          success("Produit supprimé avec succès");
        } catch (error) {
          console.error("Erreur lors de la suppression du produit:", error);
          notifyError("Erreur lors de la suppression du produit");
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  // Mettre à jour un produit
  const handleEditProduct = (product) => {
	// Assurez-vous que la catégorie est correctement extraite
    setNewProduct({
      ...product,
      category: product.category && typeof product.category === 'object' ? product.category.id : (product.category || ''),
      supplier: product.supplier && typeof product.supplier === 'object' ? product.supplier.id : (product.supplier || ''),
      currentPrice: product.currentPrice || ""
    });
    setShowAddForm(true);
  };

  // Importer un produit suggéré d'une autre structure
  const importSuggestedProduct = (suggestion) => {
    // Trouver la catégorie correspondante dans la structure courante (par nom)
    const matchingCategory = categories.find(c => c.name === suggestion.categoryName);
    // Trouver le fournisseur correspondant dans la structure courante (par nom)
    const matchingSupplier = suppliers.find(s => s.name === suggestion.supplierName);

    setNewProduct(prev => ({
      ...prev,
      name: suggestion.name,
      category: matchingCategory ? matchingCategory.id : prev.category,
      idealStock: suggestion.idealStock || prev.idealStock,
      supplier: matchingSupplier ? matchingSupplier.id : '',
    }));
    setShowSuggestions(false);
    info(`Produit importé depuis "${suggestion.structureName}". Vérifiez et ajustez les valeurs.`);
  };

  // Upload d'une fiche produit
  const handleFileUpload = async (productId, file) => {
    try {
      setUploadingFile(true);
      const response = await uploadProductFile(productId, file);
      // Mettre à jour le produit dans la liste
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, fileUrl: response.data.product.fileUrl, fileName: response.data.product.fileName, fileUploadedAt: response.data.product.fileUploadedAt } : p));
      setAllStructuresProducts(prev => prev.map(p => p.id === productId ? { ...p, fileUrl: response.data.product.fileUrl, fileName: response.data.product.fileName, fileUploadedAt: response.data.product.fileUploadedAt } : p));
      setFileModalProduct(prev => prev ? { ...prev, fileUrl: response.data.product.fileUrl, fileName: response.data.product.fileName, fileUploadedAt: response.data.product.fileUploadedAt } : prev);
      success("Fiche produit uploadée avec succès");
    } catch (err) {
      console.error("Erreur upload:", err);
      notifyError("Erreur lors de l'upload du fichier");
    } finally {
      setUploadingFile(false);
    }
  };

  // Supprimer la fiche produit
  const handleFileDelete = async (productId) => {
    try {
      await deleteProductFile(productId);
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, fileUrl: null, fileName: null, fileUploadedAt: null } : p));
      setAllStructuresProducts(prev => prev.map(p => p.id === productId ? { ...p, fileUrl: null, fileName: null, fileUploadedAt: null } : p));
      setFileModalProduct(prev => prev ? { ...prev, fileUrl: null, fileName: null, fileUploadedAt: null } : prev);
      success("Fiche produit supprimée");
    } catch (err) {
      console.error("Erreur suppression fichier:", err);
      notifyError("Erreur lors de la suppression du fichier");
    }
  };

  // Ouvrir la fenêtre de mise à jour du stock
  const openUpdateStock = async (product) => {
    try {
      // Récupérer les détails complets du produit avec l'historique
      const response = await getProductById(product.id || product.id, currentStructure.id);
      setCurrentProduct(response.data);
      setShowUpdateStock(true);
    } catch (error) {
      // En cas d'erreur, utiliser le produit de base sans historique
      setCurrentProduct(product);
      setShowUpdateStock(true);
    }
  };

  // Mettre à jour le stock
const updateProductStock = async (amount) => {
  try {
    if (!currentPerson) {
      warning("Veuillez sélectionner une personne dans la barre du haut");
      return;
    }

    const response = await updateStock(currentProduct.id, {
      amount,
      personId: currentPerson.id,
      personInitials: currentPerson.initials,
      personName: `${currentPerson.firstName} ${currentPerson.lastName}`,
      structureId: currentStructure.id
    });
    
    // Mise à jour locale des produits
    const updatedProducts = products.map(p => {
      if (p.id === currentProduct.id) {
        const updatedProduct = response.data.product;
        return {
          ...updatedProduct,
          category: typeof p.category === 'object' ?
            p.category : updatedProduct.category
        };
      }
      return p;
    });

    setProducts(updatedProducts);

    // Mettre à jour aussi les produits multi-structures
    setAllStructuresProducts(prev => prev.map(p => {
      if (p.id === currentProduct.id) {
        const updatedProduct = response.data.product;
        return { ...p, currentStock: updatedProduct.currentStock };
      }
      return p;
    }));

    // Mettre à jour le produit courant dans la modal
    setCurrentProduct(prev => prev ? { ...prev, currentStock: response.data.product.currentStock } : prev);

    // Fermer la modal
    setShowUpdateStock(false);
    setCustomAmount("");
    setCustomRemoveAmount("");

    success("Stock mis à jour avec succès");
  } catch (error) {
    console.error("Erreur lors de la mise à jour du stock:", error);
    notifyError("Erreur lors de la mise à jour du stock");
  }
};


  // Fonction pour ajouter une catégorie
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (newCategory) {
      setActionLoading(true);
      try {
        const response = await addCategory({
          name: newCategory,
          structureId: currentStructure.id
        });
        setCategories([...categories, response.data.category]);
        await refreshCategories();

        setNewCategory("");
        setShowCategoryForm(false);
        success("Catégorie ajoutée avec succès");
      } catch (error) {
        console.error("Erreur lors de l'ajout de la catégorie:", error);
        notifyError("Erreur lors de l'ajout de la catégorie");
      } finally {
        setActionLoading(false);
      }
    }
  };
    
  


  // Obtenir la classe de couleur pour l'indicateur de stock
  const getStockStatusClass = (current, ideal) => {
    if (current === 0) return "bg-red-600";
    if (current < ideal) return "bg-yellow-500";
    return "bg-green-500";
  };

  // Formatage de la date pour l'historique
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString().slice(0, 5)}`;
  };

  // Créez une fonction de rechargement des catégories
  const refreshCategories = async () => {
	  try {
		const categoriesResponse = await getCategories(currentStructure.id);
		setCategories(categoriesResponse.data);
		
		// Si vous avez un formulaire d'ajout de produit ouvert, mettez à jour sa catégorie par défaut
		if (categoriesResponse.data.length > 0) {
		  setNewProduct(prev => ({
			...prev,
			category: categoriesResponse.data[0].id
		  }));
		}
	  } catch (error) {
		console.error("Erreur lors du chargement des catégories:", error);
		setError("Erreur lors du chargement des catégories");
	  }
	};

// Fonction pour rafraîchir la liste des produits
	const refreshProducts = async () => {
	  try {
		setLoading(true);
		const productsResponse = await getProducts(currentStructure.id);
		
		// Assurez-vous que la réponse contient des données
		//console.log("Données de produits reçues:", productsResponse.data);
		
		setProducts(productsResponse.data);
		setLoading(false);
	  } catch (error) {
		console.error("Erreur lors du chargement des produits:", error);
		setError("Erreur lors du chargement des produits");
		setLoading(false);
	  }
	};

  // CSS pour le composant
  const buttonStyle = "flex items-center justify-center px-4 py-2 bg-primary text-white rounded-md shadow-sm hover:bg-primary-dark focus:outline-none";
  const buttonSecondaryStyle = "flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-md shadow-sm hover:bg-gray-700 focus:outline-none";
  const buttonDangerStyle = "flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-md shadow-sm hover:bg-red-700 focus:outline-none";
  const inputStyle = "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary";
  const tableHeaderStyle = "px-4 py-2 text-left text-gray-700 bg-gray-100";
  const tableCellStyle = "px-4 py-2 border-t";

  // Attendre que les structures soient chargées
  if (structureLoading) {
    return <LoadingSpinner size="large" text="Chargement des structures..." fullScreen />;
  }

  // Si erreur de chargement des structures (pas de structure assignée)
  if (structureError && !currentStructure) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <Package size={64} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Aucune structure assignée</h2>
          <p className="text-gray-600 mb-6">{structureError}</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
            >
              <LogOut size={20} />
              Se déconnecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner size="large" text="Chargement des données..." fullScreen />;
  }

  return (
    <div className="flex flex-col min-h-full bg-gray-50">
      {/* Notification d'erreur */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded fixed top-4 right-4 z-50">
          <span className="block sm:inline">{error}</span>
          <button className="float-right" onClick={() => setError(null)}>×</button>
        </div>
      )}


      <div className="container mx-auto p-4 flex-grow">
        {/* Mouvements du jour */}
        <DailyMovements />

        {/* Barre de recherche et contrôles */}
        <div className="bg-white rounded-lg shadow-md p-3 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Recherche */}
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un produit..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full border border-gray-300 rounded-lg py-2.5 pl-10 pr-9 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
              />
              {searchText && (
                <button onClick={() => setSearchText("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X size={16} />
                </button>
              )}
            </div>
            {/* Filtre catégorie */}
            <select
              className="border border-gray-300 rounded-lg py-2.5 px-3 text-sm bg-white focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none min-w-[160px]"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="all">Toutes les catégories</option>
              {availableCategoryNames.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            {/* Tri */}
            <select
              className="border border-gray-300 rounded-lg py-2.5 px-3 text-sm bg-white focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none min-w-[140px]"
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [newSortBy, newSortOrder] = e.target.value.split('-');
                setSortBy(newSortBy);
                setSortOrder(newSortOrder);
              }}
            >
              <option value="name-asc">Nom (A-Z)</option>
              <option value="name-desc">Nom (Z-A)</option>
              <option value="currentStock-asc">Stock croissant</option>
              <option value="currentStock-desc">Stock decroissant</option>
            </select>
          </div>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              {showAllStructures && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent/10 text-accent">
                  Toutes les structures
                </span>
              )}
              <span>{filteredProducts.length} produit{filteredProducts.length !== 1 ? 's' : ''}</span>
              {filterCategory !== "all" && (
                <button onClick={() => setFilterCategory("all")} className="text-xs text-primary hover:underline">
                  Effacer le filtre
                </button>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={expandAll} className="px-2 py-1 rounded hover:bg-gray-100 flex items-center gap-1 text-sm text-gray-500" title="Tout déplier">
                <ChevronsDown size={14} />
              </button>
              <button onClick={collapseAll} className="px-2 py-1 rounded hover:bg-gray-100 flex items-center gap-1 text-sm text-gray-500" title="Tout replier">
                <ChevronsUp size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Loading multi-structures */}
        {loadingAllStructures && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500 mb-6">
            <LoadingSpinner /> Chargement des produits de toutes les structures...
          </div>
        )}

        {/* Sections par catégorie (accordion) */}
        {!loadingAllStructures && filteredProducts.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500 mb-6">
            {searchText ? `Aucun produit ne correspond à "${searchText}"` : 'Aucun produit trouvé. Ajoutez votre premier produit !'}
          </div>
        ) : !loadingAllStructures && showAllStructures && productsByStructure ? (
          /* Mode multi-structures : un tableau par structure avec en-tête sticky + séparateurs catégorie */
          <div className="space-y-4 mb-6">
            {sortedStructureNames.map(structName => {
              const structProducts = productsByStructure[structName];
              const structKey = `struct_${structName}`;
              const isStructCollapsed = collapsedCategories[structKey];
              const structAlertCount = structProducts.filter(p => p.currentStock < p.idealStock).length;
              const catGrouped = groupByCategory(structProducts);
              const catNames = sortCategoryNames(catGrouped);

              return (
                <div key={structName}>
                  {/* Header de structure */}
                  <button
                    onClick={() => toggleCategory(structKey)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-primary/10 hover:bg-primary/15 rounded-t-lg transition-colors border-l-4 border-primary"
                  >
                    <div className="flex items-center gap-2">
                      {isStructCollapsed ? <ChevronRight size={20} className="text-primary" /> : <ChevronDown size={20} className="text-primary" />}
                      <Building2 size={18} className="text-primary" />
                      <span className="font-bold text-gray-900">{structName}</span>
                      <span className="text-sm text-gray-500">({structProducts.length} produit{structProducts.length !== 1 ? 's' : ''})</span>
                    </div>
                    {structAlertCount > 0 ? (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                        {structAlertCount} alerte{structAlertCount > 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">OK</span>
                    )}
                  </button>

                  {!isStructCollapsed && (
                    <div className="bg-white rounded-b-lg shadow-md overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full table-fixed">
                          <colgroup>
                            <col className="w-[45%]" />
                            <col className="w-[20%]" />
                            <col className="w-[35%]" />
                          </colgroup>
                          <thead className="sticky top-0 z-10">
                            <tr>
                              <th className={tableHeaderStyle}>Produit</th>
                              <th className={tableHeaderStyle}>Stock</th>
                              <th className={tableHeaderStyle}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {catNames.map(catName => {
                              const catProducts = catGrouped[catName];
                              const catKey = `${structName}_${catName}`;
                              const isCatCollapsed = collapsedCategories[catKey];
                              const alertCount = catProducts.filter(p => p.currentStock < p.idealStock).length;

                              return (
                                <React.Fragment key={catKey}>
                                  {/* Ligne séparateur catégorie */}
                                  <tr
                                    className="bg-gray-50 hover:bg-gray-100 cursor-pointer select-none"
                                    onClick={() => toggleCategory(catKey)}
                                  >
                                    <td colSpan={2} className="px-4 py-2.5 border-t border-gray-200">
                                      <div className="flex items-center gap-2">
                                        {isCatCollapsed ? <ChevronRight size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
                                        <span className="font-semibold text-gray-800">{catName}</span>
                                        <span className="text-sm text-gray-500">({catProducts.length})</span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2.5 border-t border-gray-200 text-right">
                                      {alertCount > 0 ? (
                                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                          {alertCount} alerte{alertCount > 1 ? 's' : ''}
                                        </span>
                                      ) : (
                                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">OK</span>
                                      )}
                                    </td>
                                  </tr>
                                  {/* Produits de cette catégorie */}
                                  {!isCatCollapsed && catProducts.map(product => (
                                    <tr key={product.id} className="hover:bg-gray-50">
                                      <td className={tableCellStyle}>
                                        <div className="flex items-center gap-1.5">
                                          <span>{product.name}</span>
                                          {product.fileUrl && (
                                            <button onClick={() => setFileModalProduct(product)} className="text-blue-500 hover:text-blue-700 flex-shrink-0" title="Voir la fiche produit">
                                              <Paperclip size={13} />
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                      <td className={tableCellStyle}>
                                        <div className="flex items-center">
                                          <span className="mr-2">{product.currentStock}/{product.idealStock}</span>
                                          <div className={`w-3 h-3 rounded-full ${getStockStatusClass(product.currentStock, product.idealStock)}`}></div>
                                        </div>
                                      </td>
                                      <td className={tableCellStyle}>
                                        <div className="flex items-center gap-1">
                                          <button onClick={() => openUpdateStock(product)} className="px-2 py-1 bg-primary/10 text-primary-dark rounded hover:bg-primary/20" title="Modifier le stock">
                                            <PlusCircle size={16} />
                                          </button>
                                          <button onClick={() => handleEditProduct(product)} className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200" title="Modifier le produit">
                                            <Edit size={16} />
                                          </button>
                                          {user?.role === 'admin' && (
                                            <button onMouseDown={(e) => openActionMenu(e, product)} className="px-2 py-1 bg-gray-50 text-gray-500 rounded hover:bg-gray-100" title="Plus d'actions">
                                              <MoreVertical size={16} />
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : !loadingAllStructures ? (
          /* Mode mono-structure : un seul tableau avec en-tête sticky + séparateurs catégorie */
          <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="min-w-full table-fixed">
                <colgroup>
                  <col className="w-[45%]" />
                  <col className="w-[20%]" />
                  <col className="w-[35%]" />
                </colgroup>
                <thead className="sticky top-0 z-10">
                  <tr>
                    <th className={tableHeaderStyle}>Produit</th>
                    <th className={tableHeaderStyle}>Stock</th>
                    <th className={tableHeaderStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCategoryNames.map(catName => {
                    const catProducts = productsByCategory[catName];
                    const isCollapsed = collapsedCategories[catName];
                    const alertCount = catProducts.filter(p => p.currentStock < p.idealStock).length;

                    return (
                      <React.Fragment key={catName}>
                        {/* Ligne séparateur catégorie */}
                        <tr
                          className="bg-gray-50 hover:bg-gray-100 cursor-pointer select-none"
                          onClick={() => toggleCategory(catName)}
                        >
                          <td colSpan={2} className="px-4 py-2.5 border-t border-gray-200">
                            <div className="flex items-center gap-2">
                              {isCollapsed ? <ChevronRight size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
                              <span className="font-semibold text-gray-800">{catName}</span>
                              <span className="text-sm text-gray-500">({catProducts.length})</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 border-t border-gray-200 text-right">
                            {alertCount > 0 ? (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                {alertCount} alerte{alertCount > 1 ? 's' : ''}
                              </span>
                            ) : (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">OK</span>
                            )}
                          </td>
                        </tr>
                        {/* Produits de cette catégorie */}
                        {!isCollapsed && catProducts.map(product => (
                          <tr key={product.id} className="hover:bg-gray-50">
                            <td className={tableCellStyle}>
                              <div className="flex items-center gap-1.5">
                                <span>{product.name}</span>
                                {product.fileUrl && (
                                  <button onClick={() => setFileModalProduct(product)} className="text-blue-500 hover:text-blue-700 flex-shrink-0" title="Voir la fiche produit">
                                    <Paperclip size={13} />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className={tableCellStyle}>
                              <div className="flex items-center">
                                <span className="mr-2">{product.currentStock}/{product.idealStock}</span>
                                <div className={`w-3 h-3 rounded-full ${getStockStatusClass(product.currentStock, product.idealStock)}`}></div>
                              </div>
                            </td>
                            <td className={tableCellStyle}>
                              <div className="flex items-center gap-1">
                                <button onClick={() => openUpdateStock(product)} className="px-2 py-1 bg-primary/10 text-primary-dark rounded hover:bg-primary/20" title="Modifier le stock">
                                  <PlusCircle size={16} />
                                </button>
                                <button onClick={() => handleEditProduct(product)} className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200" title="Modifier le produit">
                                  <Edit size={16} />
                                </button>
                                {user?.role === 'admin' && (
                                  <button onMouseDown={(e) => openActionMenu(e, product)} className="px-2 py-1 bg-gray-50 text-gray-500 rounded hover:bg-gray-100" title="Plus d'actions">
                                    <MoreVertical size={16} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        
        {/* Légende */}
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
          <h3 className="font-semibold mb-2">Légende</h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
              <span>Stock suffisant</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
              <span>Stock faible</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-red-600 mr-2"></div>
              <span>Rupture de stock</span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Ajouter/Éditer produit */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
            <div className="p-4 border-b">
              <h2 className="text-xl font-semibold">
                {newProduct.id ? "Modifier le produit" : "Ajouter un produit"}
              </h2>
            </div>
            <form onSubmit={handleAddProduct} className="p-4">
              <div className="mb-4 relative">
                <label className="block text-gray-700 mb-2">Nom du produit</label>
                <input
                  type="text"
                  className={inputStyle}
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                  onFocus={() => { if (productSuggestions.length > 0) setShowSuggestions(true); }}
                  placeholder="Nom du produit"
                  required
                  autoComplete="off"
                />
                {/* Suggestions cross-structure */}
                {showSuggestions && productSuggestions.length > 0 && !newProduct.id && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    <div className="px-3 py-1.5 bg-gray-50 border-b text-xs text-gray-500 font-medium">
                      Existe dans d'autres structures
                    </div>
                    {productSuggestions.map(suggestion => (
                      <button
                        key={suggestion.id}
                        type="button"
                        onClick={() => importSuggestedProduct(suggestion)}
                        className="w-full text-left px-3 py-2 hover:bg-primary/5 border-b last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-sm text-gray-900">{suggestion.name}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-2">
                          <span><Building2 size={10} className="inline mr-0.5" />{suggestion.structureName}</span>
                          {suggestion.categoryName && <span>Cat: {suggestion.categoryName}</span>}
                          <span>Stock idéal: {suggestion.idealStock}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Catégorie</label>
                <select 
                  className={inputStyle}
                  value={newProduct.category}
                  onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                  required
                >
                  {categories.map(cat => (
                    <option key={cat.id || cat} value={cat.id || cat}>
                      {cat.name || cat}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 mb-2">Stock actuel</label>
                  <input
                    type="number"
                    className={inputStyle}
                    value={newProduct.currentStock}
                    onChange={(e) => setNewProduct({...newProduct, currentStock: parseInt(e.target.value) || 0})}
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">Stock idéal</label>
                  <input
                    type="number"
                    className={inputStyle}
                    value={newProduct.idealStock}
                    onChange={(e) => setNewProduct({...newProduct, idealStock: parseInt(e.target.value) || 0})}
                    min="1"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 mb-2">Fournisseur</label>
                  <select
                    className={inputStyle}
                    value={newProduct.supplier || ""}
                    onChange={(e) => setNewProduct({...newProduct, supplier: e.target.value})}
                  >
                    <option value="">Aucun fournisseur</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">Prix actuel (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    className={inputStyle}
                    value={newProduct.currentPrice || ""}
                    onChange={(e) => setNewProduct({...newProduct, currentPrice: e.target.value})}
                    min="0"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button 
                  type="button"
                  className={buttonSecondaryStyle}
                  onClick={() => {
                    setShowAddForm(false);
                    if (!newProduct.id) {
                      setNewProduct({
                        name: "",
                        category: categories.length > 0 ? categories[0].id || categories[0] : "",
                        idealStock: 10,
                        currentStock: 0,
                        supplier: "",
                        currentPrice: ""
                      });
                    }
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className={buttonStyle}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <LoadingSpinner size="small" />
                  ) : (
                    newProduct.id ? "Mettre à jour" : "Ajouter"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


{/* Modal Mettre à jour le stock */}
{showUpdateStock && currentProduct && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
      <div className="p-4 border-b">
        <h2 className="text-xl font-semibold">Mettre à jour le stock</h2>
        <p className="text-gray-600">{currentProduct.name}</p>
      </div>
      <div className="p-4">
        <div className="mb-6 text-center">
          <p className="text-gray-600 mb-2">Stock actuel</p>
          <div className="text-4xl font-bold">{currentProduct.currentStock}</div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-gray-600 mb-2 text-center">Ajouter</p>
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-2 gap-2">
                <button 
                  className="bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg text-xl"
                  onClick={() => updateProductStock(1)}
                >
                  +1
                </button>
                <button 
                  className="bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg text-xl"
                  onClick={() => updateProductStock(5)}
                >
                  +5
                </button>
              </div>
              <div className="relative mt-2">
                <input 
                  type="number" 
                  className="w-full px-2 py-3 border border-gray-300 rounded-lg text-center"
                  placeholder="Quantité personnalisée"
                  min="1"
                  value={customAmount || ""}
                  onChange={(e) => setCustomAmount(parseInt(e.target.value) || "")}
                />
                <button 
                  className="absolute top-0 right-0 h-full px-4 bg-green-600 hover:bg-green-700 text-white rounded-r-lg"
                  onClick={() => {
                    if (customAmount > 0) {
                      updateProductStock(customAmount);
                      setCustomAmount("");
                    }
                  }}
                  disabled={!customAmount || customAmount <= 0}
                >
                  +
                </button>
              </div>
            </div>
          </div>
          
          <div>
            <p className="text-gray-600 mb-2 text-center">Retirer</p>
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-2 gap-2">
                <button 
                  className="bg-red-500 hover:bg-red-600 text-white py-3 rounded-lg text-xl"
                  onClick={() => updateProductStock(-1)}
                  disabled={currentProduct.currentStock < 1}
                >
                  -1
                </button>
                <button 
                  className="bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg text-xl"
                  onClick={() => updateProductStock(-5)}
                  disabled={currentProduct.currentStock < 5}
                >
                  -5
                </button>
              </div>
              <div className="relative mt-2">
                <input 
                  type="number" 
                  className="w-full px-2 py-3 border border-gray-300 rounded-lg text-center"
                  placeholder="Quantité personnalisée"
                  min="1"
                  max={currentProduct.currentStock}
                  value={customRemoveAmount || ""}
                  onChange={(e) => setCustomRemoveAmount(parseInt(e.target.value) || "")}
                />
                <button 
                  className="absolute top-0 right-0 h-full px-4 bg-red-600 hover:bg-red-700 text-white rounded-r-lg"
                  onClick={() => {
                    if (customRemoveAmount > 0 && customRemoveAmount <= currentProduct.currentStock) {
                      updateProductStock(-customRemoveAmount);
                      setCustomRemoveAmount("");
                    }
                  }}
                  disabled={!customRemoveAmount || customRemoveAmount <= 0 || customRemoveAmount > currentProduct.currentStock}
                >
                  -
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
		  <h3 className="font-semibold mb-2">Historique des modifications</h3>
		  <div className="max-h-48 overflow-y-auto border rounded-md">
			{!currentProduct.history || currentProduct.history.length === 0 ? (
			  <p className="p-3 text-gray-500 text-center">Aucun historique disponible</p>
			) : (
			  <table className="min-w-full">
				<thead>
				  <tr className="bg-gray-50">
					<th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
					<th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Personne</th>
					<th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Modification</th>
				  </tr>
				</thead>
				<tbody>
				  {currentProduct.history.map((entry, index) => (
					<tr key={index} className="border-t">
					  <td className="px-3 py-2 text-sm">{formatDate(entry.timestamp)}</td>
					  <td className="px-3 py-2 text-sm">
						{/* Utilisez les nouvelles propriétés pour afficher la personne */}
						{entry.personInitials || entry.personName || entry.userName || "Admin"}
					  </td>
					  <td className={`px-3 py-2 text-sm text-right ${entry.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
						{entry.amount > 0 ? `+${entry.amount}` : entry.amount}
					  </td>
					</tr>
				  ))}
				</tbody>
			  </table>
			)}
		  </div>
		</div>
        
        <div className="flex justify-end">
          <button 
            className={buttonSecondaryStyle}
            onClick={() => {
              setShowUpdateStock(false);
              setCustomAmount("");
              setCustomRemoveAmount("");
            }}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  </div>
)}



      {/* Modal Liste de courses */}
      {showShoppingList && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">Liste de courses</h2>
              <div>
                <button
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => info("Export PDF (fonctionnalité à venir)")}
                  title="Exporter en PDF"
                >
                  <span className="mr-2">PDF</span>
                </button>
                <button
                  className="text-gray-500 hover:text-gray-700 ml-2"
                  onClick={() => info("Export Excel (fonctionnalité à venir)")}
                  title="Exporter en Excel"
                >
                  <span>Excel</span>
                </button>
              </div>
            </div>
            <div className="p-4">
              {shoppingList.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                  <ShoppingCart size={48} className="mx-auto mb-4 text-gray-400" />
                  <p>Tous les stocks sont à des niveaux optimaux !</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 text-left">Produit</th>
                        <th className="px-4 py-2 text-left">Catégorie</th>
                        <th className="px-4 py-2 text-center">Stock actuel</th>
                        <th className="px-4 py-2 text-center">Stock idéal</th>
                        <th className="px-4 py-2 text-center">À commander</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shoppingList.map(item => (
                        <tr key={item.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-2">{item.name}</td>
                          <td className="px-4 py-2">
                            {typeof item.category === 'object' 
                              ? item.category?.name 
                              : getCategoryName(item.category)}
                          </td>
                          <td className="px-4 py-2 text-center">
                            <span className={`${item.currentStock === 0 ? 'text-red-600' : 'text-yellow-600'}`}>
                              {item.currentStock}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-center">{item.idealStock}</td>
                          <td className="px-4 py-2 text-center font-bold">{item.toOrder}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-gray-50">
                        <td colSpan="4" className="px-4 py-2 text-right font-semibold">Nombre total de produits à commander :</td>
                        <td className="px-4 py-2 text-center font-bold">
                          {shoppingList.reduce((sum, item) => sum + item.toOrder, 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
              
              <div className="flex justify-end mt-6">
                <button 
                  className={buttonSecondaryStyle}
                  onClick={() => setShowShoppingList(false)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajouter catégorie */}
      {showCategoryForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
            <div className="p-4 border-b">
              <h2 className="text-xl font-semibold">Ajouter une catégorie</h2>
            </div>
            <form onSubmit={handleAddCategory} className="p-4">
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Nom de la catégorie</label>
                <input 
                  type="text" 
                  className={inputStyle}
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Nom de la catégorie"
                  required
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button 
                  type="button"
                  className={buttonSecondaryStyle}
                  onClick={() => {
                    setShowCategoryForm(false);
                    setNewCategory("");
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className={buttonStyle}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <LoadingSpinner size="small" />
                  ) : (
                    "Ajouter"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Historique des prix */}
      {showPriceHistory && priceHistoryProduct && (
        <PriceHistory
          product={priceHistoryProduct}
          selectedPerson={currentPerson}
          onClose={() => {
            setShowPriceHistory(false);
            setPriceHistoryProduct(null);
          }}
          onPriceAdded={async () => {
            // Refresh products list
            const productsResponse = await getProducts(currentStructure.id);
            setProducts(productsResponse.data);
          }}
        />
      )}

      {/* Modal Fiche produit (upload/preview) */}
      {fileModalProduct && (() => {
        const fileUrl = fileModalProduct.fileUrl
          ? `${process.env.REACT_APP_API_URL?.replace('/api', '') || ''}${fileModalProduct.fileUrl}`
          : null;
        const isPdf = fileModalProduct.fileName?.toLowerCase().endsWith('.pdf');
        const isImage = /\.(jpg|jpeg|png)$/i.test(fileModalProduct.fileName || '');
        const isAdmin = user?.role === 'admin';

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className={`bg-white rounded-lg shadow-lg w-full ${fileUrl ? 'max-w-2xl max-h-[90vh] flex flex-col' : 'max-w-sm'}`}>
              <div className="p-4 border-b flex justify-between items-center flex-shrink-0">
                <h2 className="text-lg font-semibold truncate pr-2">Fiche produit : {fileModalProduct.name}</h2>
                <button onClick={() => setFileModalProduct(null)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                  <X size={20} />
                </button>
              </div>
              <div className={`p-4 ${fileUrl ? 'flex-1 overflow-y-auto min-h-0' : ''}`}>
                {fileUrl ? (
                  /* Fichier existant — preview + actions */
                  <div className="space-y-3">
                    {/* Preview inline */}
                    {isPdf ? (
                      <iframe
                        src={fileUrl}
                        className="w-full rounded-lg border border-gray-200"
                        style={{ height: '60vh' }}
                        title={fileModalProduct.fileName}
                      />
                    ) : isImage ? (
                      <img
                        src={fileUrl}
                        alt={fileModalProduct.fileName}
                        className="w-full max-h-[60vh] object-contain rounded-lg border border-gray-200 bg-gray-50"
                      />
                    ) : null}
                    {/* Infos fichier */}
                    <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                      <Paperclip size={16} className="text-blue-600 flex-shrink-0" />
                      <p className="text-sm text-gray-700 truncate flex-1">{fileModalProduct.fileName}</p>
                      {fileModalProduct.fileUploadedAt && (
                        <p className="text-xs text-gray-500 flex-shrink-0">
                          {new Date(fileModalProduct.fileUploadedAt).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                    {/* Actions */}
                    <div className="flex gap-2">
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                      >
                        <Download size={14} />
                        Télécharger
                      </a>
                      {isAdmin && (
                        <button
                          onClick={() => handleFileDelete(fileModalProduct.id)}
                          className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition text-sm"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    {isAdmin && (
                      <div className="border-t pt-3">
                        <label className="block text-xs text-gray-500 mb-1">Remplacer le fichier</label>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            if (e.target.files[0]) handleFileUpload(fileModalProduct.id, e.target.files[0]);
                          }}
                          className="block w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                          disabled={uploadingFile}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  /* Pas de fichier */
                  isAdmin ? (
                    <div className="space-y-3">
                      <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer"
                        onClick={() => document.getElementById('file-upload-input').click()}
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
                        onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                          if (e.dataTransfer.files[0]) handleFileUpload(fileModalProduct.id, e.dataTransfer.files[0]);
                        }}
                      >
                        <Upload size={24} className="mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">Glissez un fichier ici</p>
                        <p className="text-xs text-gray-400 mt-1">ou cliquez pour choisir</p>
                        <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG (max 5 Mo)</p>
                      </div>
                      <input
                        id="file-upload-input"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files[0]) handleFileUpload(fileModalProduct.id, e.target.files[0]);
                        }}
                        disabled={uploadingFile}
                      />
                      {uploadingFile && (
                        <div className="text-center text-sm text-gray-500">
                          <LoadingSpinner size="small" /> Upload en cours...
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-6">
                      <Paperclip size={24} className="mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">Aucune fiche produit</p>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      {/* Floating Action Button - Ajouter un produit */}
      {!showAllStructures && (
        <button
          onClick={() => setShowAddForm(true)}
          className="fixed bottom-6 right-6 z-30 bg-primary hover:bg-primary-dark text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg hover:shadow-xl transition-all active:scale-95"
          title="Ajouter un produit"
        >
          <PlusCircle size={28} />
        </button>
      )}

      {/* Menu contextuel admin (portal pour éviter le clipping overflow) */}
      {actionMenu && createPortal(
        <div
          id="action-menu-portal"
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]"
          style={{
            top: actionMenu.y,
            left: Math.min(actionMenu.x, window.innerWidth - 170),
            transform: 'translateX(-100%)',
            zIndex: 9999,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button onClick={() => { setPriceHistoryProduct(actionMenu.product); setShowPriceHistory(true); setActionMenu(null); }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2">
            <TrendingUp size={14} className="text-green-600" /> Historique prix
          </button>
          <button onClick={() => { setFileModalProduct(actionMenu.product); setActionMenu(null); }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2">
            <Paperclip size={14} className="text-blue-600" /> {actionMenu.product.fileUrl ? 'Gérer la fiche' : 'Ajouter une fiche'}
          </button>
          <hr className="my-1 border-gray-100" />
          <button onClick={() => { handleDeleteProduct(actionMenu.product.id); setActionMenu(null); }} className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 text-red-600 flex items-center gap-2">
            <Trash2 size={14} /> Supprimer
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default StockManagementApp;